import epsClient from 'enrollment-platform/api/eps.client'
import { type SurecoCsvDataType } from '../constants'
import type { StartCompareRunResponse } from './compare-run-api'

const COMPARE_DATA_BASE_PATH = '/api/v1/plan-management-v2/compare-data'

/** summary of a COMPLETED CSV compare run. */
export type AnalyzeCompareResult = {
  runId: string
  dataType: SurecoCsvDataType
  year: number
  totalCsvRows: number
  newCount: number
  modifiedCount: number
  unchangedCount: number
  errorCount: number
  originalS3Key: string
  /** Unchanged rows per state — they aren't stored as rows. */
  unchangedByState?: Record<string, number>
}

/** Per-row validation payload from GET compare-data (`errors` column). */
export type CsvCompareRowErrors = {
  line?: number
  error?: string
  fieldErrors?: Record<string, string> | null
}

export type CsvCompareFieldResult = {
  csvValue: unknown
  liveValue: unknown
  isModified: boolean
}

export type CsvCompareRow = {
  id: string
  naturalKeyHash?: string
  compareStatus: 'NEW' | 'MODIFIED' | 'ERROR'
  /**
   * Per-field { csvValue, liveValue, isModified }, computed server-side via
   * the same comparator the apply path uses — populated for every row
   * (NEW, MODIFIED, and ERROR alike) across all 5 data types. This is the
   * only source of field values; there is no separate raw uploadedData/
   * liveData payload.
   */
  fieldCompare: Record<string, CsvCompareFieldResult>
  /** Current EPS shape — `{ line, fieldErrors }` (and optional `error`). */
  errors: CsvCompareRowErrors | null
  state: string | null
  hiosPlanId: string | null
  issuerId: string | null
}

export type CsvComparePageResult = {
  items: CsvCompareRow[]
  total: number
  page: number
  limit: number
}

export async function presignCompareDataUpload(params: {
  originalFilename: string
  fileSize: number
  dataType: SurecoCsvDataType
}): Promise<{ folder: string; uploadUrl: string }> {
  const { data } = await epsClient.post<{ folder: string; uploadUrl: string }>(
    `${COMPARE_DATA_BASE_PATH}/presigned-upload-url`,
    params
  )
  return data
}

export async function analyzeCompareData(params: {
  folder: string
  originalFilename: string
  dataType: SurecoCsvDataType
}): Promise<StartCompareRunResponse> {
  const response = await epsClient.post<StartCompareRunResponse>(
    `${COMPARE_DATA_BASE_PATH}/analyze`,
    {
      folder: params.folder,
      originalFilename: params.originalFilename,
      dataType: params.dataType,
    }
  )
  return response.data
}

/**
 * Grid columns are bound to `fieldCompare.<key>.<side>`; the backend sorts by
 * the plain field key (or a direct column like compareStatus).
 */
export function toSortBy(colId: string | undefined): string | undefined {
  if (!colId) return undefined
  return colId.startsWith('fieldCompare.') ? colId.split('.')[1] : colId
}

export async function getCompareData(params: {
  runId: string
  dataType: SurecoCsvDataType
  page: number
  limit: number
  compareStatus?: string
  state?: string
  /** Empty/omitted = all states. */
  states?: string[]
  hiosPlanId?: string
  /** Prefix of a HIOS Plan ID or issuer ID. */
  hiosSearch?: string
  issuerId?: string
  sortBy?: string
  sortOrder?: 'asc' | 'desc'
  filterField?: string
  filterValue?: string
}): Promise<CsvComparePageResult> {
  const sortBy = toSortBy(params.sortBy)
  const response = await epsClient.get(COMPARE_DATA_BASE_PATH, {
    params: {
      runId: params.runId,
      dataType: params.dataType,
      page: params.page,
      limit: params.limit,
      ...(params.compareStatus ? { compareStatus: params.compareStatus } : {}),
      ...(params.state ? { state: params.state } : {}),
      ...(params.states && params.states.length > 0
        ? { states: params.states.join(',') }
        : {}),
      ...(params.hiosPlanId ? { hiosPlanId: params.hiosPlanId } : {}),
      ...(params.hiosSearch ? { hiosSearch: params.hiosSearch } : {}),
      ...(params.issuerId ? { issuerId: params.issuerId } : {}),
      ...(sortBy ? { sortBy } : {}),
      ...(params.sortOrder ? { sortOrder: params.sortOrder } : {}),
      ...(params.filterField ? { filterField: params.filterField } : {}),
      ...(params.filterValue ? { filterValue: params.filterValue } : {}),
    },
  })
  return response.data
}

export type CsvCompareStateCounts = {
  newCount: number
  modifiedCount: number
  errorCount: number
}

/** Stored-row counts of a CSV run, overall and per state ('' = no state). */
export async function getCompareCounts(params: {
  runId: string
  dataType: SurecoCsvDataType
}): Promise<
  CsvCompareStateCounts & {
    total: number
    byState: Record<string, CsvCompareStateCounts>
  }
> {
  const response = await epsClient.get(`${COMPARE_DATA_BASE_PATH}/counts`, {
    params: { runId: params.runId, dataType: params.dataType },
  })
  return response.data
}

/**
 * Downloads the compare report (.xlsx — Summary, Changes, Field changes,
 * Errors) for a run of either compare, honouring the grid's filters.
 */
export async function exportCompareReport(params: {
  runId: string
  dataType: SurecoCsvDataType
  /** Comma-separated stored statuses; omit for everything. */
  compareStatus?: string
  /** Empty/omitted = all states. */
  states?: string[]
  hiosPlanId?: string
  /** Prefix of a HIOS Plan ID or issuer ID. */
  hiosSearch?: string
}): Promise<void> {
  const response = await epsClient.get(`${COMPARE_DATA_BASE_PATH}/export`, {
    params: {
      runId: params.runId,
      dataType: params.dataType,
      ...(params.compareStatus ? { compareStatus: params.compareStatus } : {}),
      ...(params.states && params.states.length > 0
        ? { states: params.states.join(',') }
        : {}),
      ...(params.hiosPlanId ? { hiosPlanId: params.hiosPlanId } : {}),
      ...(params.hiosSearch ? { hiosSearch: params.hiosSearch } : {}),
    },
    responseType: 'blob',
  })
  const disposition = response.headers?.['content-disposition']
  const match =
    typeof disposition === 'string'
      ? /filename="?([^"]+)"?/.exec(disposition)
      : null
  const filename = match?.[1] ?? `${params.dataType}-compare-report.xlsx`
  const url = window.URL.createObjectURL(new Blob([response.data]))
  const link = document.createElement('a')
  link.href = url
  link.setAttribute('download', filename)
  document.body.appendChild(link)
  link.click()
  document.body.removeChild(link)
  window.URL.revokeObjectURL(url)
}
